import React, { useState } from "react";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import { Link, useSearchParams } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import { useMutation, useQueryClient } from "react-query";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import AspectRatio from "@mui/joy/AspectRatio";
import axios from "axios";
import { Buffer } from "buffer";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Stack from "@mui/material/Stack";
import { useSectionFile } from "../hooks/useSections";

const onSuccess = (query) => (data) => {
  switch (query) {
    case "sections-get":
      console.log("Pic fetched", data);
      break;
    default:
      console.log("fetch pic success");
  }
};

const onError = (query) => (error) => {
  switch (query) {
    case "sections-get":
      console.log("Pic error", error);
      break;
    default:
      console.log("some error on fetching pic");
  }
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function SectionListTile({
  token,
  title,
  subtitle,
  sectionId,
  imageFileName,
}) {
  const [searchParams] = useSearchParams();

  const homeItemType = searchParams.get("homeItemType");

  const [dialogState, setDialogState] = useState({ open: false, text: "" });

  const pic = useSectionFile(
    onSuccess,
    onError,
    token.token,
    imageFileName,
    homeItemType
  );

  const queryClient = useQueryClient();

  const deleteSection = useMutation(
    (sectionId) => {
      const headers = {
        Accept: "*/*",
        token: token.token,
      };

      return axios.delete(
        `${process.env.REACT_APP_BASE_URL}:${process.env.REACT_APP_ADMIN_PORT}/sections/delete_section/${sectionId}`,
        {
          headers,
        }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("sections-get");
      },
    }
  );

  const handleTextChange = (event) => {
    setDialogState({ ...dialogState, text: event.target.value });
  };

  const handleClickOpen = (sectionId, sectionTitle) => {
    setDialogState({ open: true, text: "" });
  };

  const handleClose = () => {
    setDialogState({ open: false, text: "" });
  };

  const handleDelete = (sectionId) => {
    console.log("deleting section");
    handleClose();
    console.log("deleting section");
    deleteSection.mutate(sectionId);
  };

  return (
    <div>
      <ImageListItem>
        <AspectRatio
          objectFit="cover"
          ratio="6/5"
          sx={{
            width: 300,
            bgcolor: "background.level2",
            borderRadius: 10,
          }}
        >
          {pic.isLoading ? (
            <Skeleton height="185px" highlightColor="#ffffff" />
          ) : pic.isError || !pic.data ? (
            <img
              style={{ opacity: null }}
              src="/img/placeholder.png"
              alt="Cannot be displayed..."
            />
          ) : (
            <img
              style={{ opacity: null }}
              src={`data:image/png;base64,${Buffer.from(pic.data).toString(
                "base64"
              )}`}
              alt="Cannot be displayed..."
            />
          )}
        </AspectRatio>
        <ImageListItemBar
          title={
            <Stack direction="row" alignItems="center">
              <span>{title}</span>
            </Stack>
          }
          sx={{
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
          subtitle={subtitle}
          actionIcon={
            <Stack direction="row" spacing={1}>
              <IconButton
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                aria-label={`navigate to ${title}`}
                component={Link}
                to={`/editSection/${sectionId}?homeItemType=${homeItemType}`}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                aria-label={`delete ${title}`}
                onClick={() =>
                  deleteSection.isLoading
                    ? null
                    : handleClickOpen(sectionId, title)
                }
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          }
        />
      </ImageListItem>
      <Dialog open={dialogState.open} onClose={handleClose}>
        <DialogTitle>Delete this place?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Unexpected bad things will happen if you don’t read this!
          </DialogContentText>
          <DialogContentText>
            This action cannot be undone. This will permanently delete {title}{" "}
            section. Please type {title}/{sectionId}/delete-this-section.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="delete-text"
            fullWidth
            variant="standard"
            onChange={handleTextChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={() => handleDelete(sectionId)}
            disabled={
              dialogState.text !== `${title}/${sectionId}/delete-this-section`
            }
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={deleteSection.isError}
        autoHideDuration={2000}
        onClose={() => deleteSection.reset()}
      >
        <Alert
          onClose={() => deleteSection.reset()}
          severity="error"
          sx={{ width: "100%" }}
        >
          Delete failed!
        </Alert>
      </Snackbar>
    </div>
  );
}
